// Lib
import React from 'react';
import PropTypes from 'prop-types';

// Utils
import platformSingleton from '../../../platform/platformSingleton';
import rafThrottle from '../../../../common/utils/lib/rafThrottle';
import * as pointLib from '../../../../common/maths/geometry/point';
import { getUpdatedElementDragLayerData } from './elementDragLayerStateManagerUtils';
import { getHasOutstandingChanges, NO_ROTATION } from '../../../element/dnd/utils/elementDragAnimationUtils';
import measurementsRegistry from '../../../components/measurementsStore/measurementsRegistry';
import { Stopwatch } from '../../../debug/performance/stopwatch';

// Components
import ElementDragLayerDisplay from './ElementDragLayerDisplay';

// State
import { updateDragAndDropStateSingletonFromDragLayerData } from '../../../element/dnd/dragAndDropStateSingleton';

/**
 * Similar to the renderThrottleDragLayer however this also calculates
 * the group position and element rotation, because it's performance sensitive.
 */
export default class ElementDragLayerStateManager extends React.Component {
    state = {
        elementRotation: NO_ROTATION,
    };

    stopwatch = new Stopwatch('ElementDragLayerStateManager', 1 / 20);

    componentWillMount() {
        const {
            item,
            sourceClientOffset,
            usingHookDragLayer = false,
            usingHookDragSources = false,
            usingHookDropTarget = false,
        } = this.props;
        const { scaledCustomDragOffset = { x: 0, y: 0 }, zoomScaleOnDragSource } = item;

        this.stopwatch.start({
            elementCount: item.draggedElementIds.length,
            connectedElementCount: item.connectingElementIds?.length,
            zoom: item.zoomScaleOnDragSource,
            usingHookDragLayer,
            usingHookDropTarget,
            usingHookDragSources,
        });

        this.setState({
            ...this.props,
            groupPosition: pointLib.translate(sourceClientOffset, scaledCustomDragOffset),
            shadowIntensity: 0,
            zoomScaleOnDragSource,
        });

        // Account for changes in scroll
        this.initialCanvasScroll = this.getMemoisedCanvasScroll();
        const canvasViewport = measurementsRegistry.getCanvasViewportDomElement();
        if (canvasViewport) canvasViewport.addEventListener('scroll', this.clearMemoisedCanvasScroll);

        const updatedDragLayerData = this.calculateUpdatedDragLayerData(this.props, 0);

        updateDragAndDropStateSingletonFromDragLayerData(updatedDragLayerData, performance.now());
    }

    componentWillReceiveProps(nextProps) {
        this.throttledCalculateState(nextProps);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return this.state !== nextState;
    }

    componentWillUnmount() {
        this.throttledCalculateState.cancel();

        const canvasViewport = measurementsRegistry.getCanvasViewportDomElement();
        if (canvasViewport) canvasViewport.removeEventListener('scroll', this.clearMemoisedCanvasScroll);

        this.stopwatch.finish();
    }

    getMemoisedCanvasScroll = () => {
        if (this.memoisedCanvasScroll) return this.memoisedCanvasScroll;

        this.memoisedCanvasScroll = measurementsRegistry.getCanvasViewportScrollAsPoint();
        return this.memoisedCanvasScroll;
    };

    clearMemoisedCanvasScroll = () => {
        this.memoisedCanvasScroll = null;
    };

    calculateUpdatedDragLayerData = (nextProps, dt) => {
        const {
            sourceClientOffset,
            clientOffset,
            item,
            movementOffset,
            attachModeHoveredId,
            dragModifierKeys,
            areDnd3dEffectsEnabled,
        } = nextProps;
        const { element, draggedElements, scaledCustomDragOffset = { x: 0, y: 0 }, isNewElement } = item;

        const currentScroll = this.getMemoisedCanvasScroll();

        return getUpdatedElementDragLayerData(
            this.state,
            dt,
            this.initialCanvasScroll,
            currentScroll,
            element,
            isNewElement,
            draggedElements,
            sourceClientOffset,
            scaledCustomDragOffset,
            movementOffset,
            dragModifierKeys,
            clientOffset,
            areDnd3dEffectsEnabled,
            platformSingleton.features.isTouch,
            attachModeHoveredId,
        );
    };

    calculateState = (nextProps) => {
        const { sourceClientOffset, item } = nextProps;

        this.stopwatch.markFrame();

        if (!sourceClientOffset || !item) return;

        // Account for framerate differences
        const now = performance.now();
        const dt = this.lastTimestamp ? now - this.lastTimestamp : undefined;
        this.lastTimestamp = now;

        const updatedDragLayerData = this.calculateUpdatedDragLayerData(nextProps, dt);

        // The drop state singleton is used to share data from the drag layer to the dropped element
        // It's a hack, but it's the best I could think of
        updateDragAndDropStateSingletonFromDragLayerData(updatedDragLayerData, now);

        // Prevent performance regressions by getting a slice of the state
        // Not sure why this is so important at the moment, but if this gets modified ensure that
        //  dragging continues to operate at 120hz
        const nextState = {
            ...nextProps,
            groupPosition: updatedDragLayerData.groupPosition,
            elementRotation: updatedDragLayerData.elementRotation,
            shadowIntensity: updatedDragLayerData.shadowIntensity,
            dragAndDropMode: updatedDragLayerData.dragAndDropMode,
            snapMode: updatedDragLayerData.snapMode,
            interpolationStyle: updatedDragLayerData.interpolationStyle,
        };

        // Schedule a throttled update
        const hasOutstandingChanges =
            getHasOutstandingChanges(updatedDragLayerData.elementRotation, updatedDragLayerData.positionDelta) ||
            // Animate the shadow if it still needs it
            (updatedDragLayerData.shadowIntensity < 1 &&
                updatedDragLayerData.shadowIntensity < updatedDragLayerData.desiredShadowIntensity);

        // TODO - Turns out passing the nextState here is very important, otherwise the frames
        //   take twice as long. Not 100% sure why that is at the moment but will continue to monitor
        if (hasOutstandingChanges) this.throttledCalculateState(nextState);

        // Cause the re-render
        this.setState(nextState);
    };

    throttledCalculateState = rafThrottle(this.calculateState);

    render() {
        return <ElementDragLayerDisplay {...this.state} />;
    }
}

ElementDragLayerStateManager.propTypes = {
    item: PropTypes.object,
    sourceClientOffset: PropTypes.object,
    clientOffset: PropTypes.object,
    movementOffset: PropTypes.object,
    attachModeHoveredId: PropTypes.string,
    dragModifierKeys: PropTypes.object,
    areDnd3dEffectsEnabled: PropTypes.bool,
    // just for tracking stats behind the drag update feature flags
    // remove these props when the old code is removed
    usingHookDragLayer: PropTypes.bool,
    usingHookDragSources: PropTypes.bool,
    usingHookDropTarget: PropTypes.bool,
};
